/**
 * Procure to Pay Compliance Agent — 39 fraud / anomaly use cases grouped by
 * domain. Click any use case to fetch a sample of the relevant Oracle Fusion
 * records (via the user's saved Oracle profile) and inspect them inline.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useErp } from "@/context/ErpContext";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  ShieldWarning, Play, CaretRight, Warning, CheckCircle, Gear, Sparkle, Target,
} from "@phosphor-icons/react";

const RISK_COLOR = {
  Critical: "border-red-300 bg-red-50 text-[#DC2626]",
  High:     "border-amber-300 bg-amber-50 text-amber-700",
  Medium:   "border-blue-300 bg-[#0B1E3F]/5 text-[#0B1E3F]",
  Low:      "border-slate-300 bg-slate-50 text-slate-700",
};
const SEVERITY_BG = {
  Critical: "bg-[#DC2626]",
  High:     "bg-amber-600",
  Medium:   "bg-[#0B1E3F]",
  Low:      "bg-slate-500",
};
const FEAS_COLOR = {
  Yes:     "text-emerald-600",
  Partial: "text-amber-600",
  No:      "text-[#DC2626]",
};

export default function P2PCompliancePage() {
  const { setSetupOpen } = useErp();
  const [catalog, setCatalog] = useState({ domains: [], use_cases: [] });
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const profileMissing = /oracle profile/i.test(err);

  useEffect(() => {
    api.get("/p2p-compliance/catalog").then((r) => setCatalog(r.data));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    (catalog.use_cases || []).forEach((u) => { (g[u.domain] = g[u.domain] || []).push(u); });
    return g;
  }, [catalog]);

  const run = async (uc) => {
    setSelected(uc); setResult(null); setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/p2p-compliance/${uc.id}/run`);
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't run this use case.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Compliance Agent"
        title="Procure to Pay Compliance"
        subtitle="39 AI-powered fraud and anomaly detection use cases across the P2P process, connected live to Oracle Fusion."
        testid="p2p-title"
      />
      <div className="p-8 grid lg:grid-cols-[420px_1fr] gap-6">
        {/* Left — use case catalog */}
        <aside data-testid="p2p-catalog">
          {(catalog.domains || []).map((d) => (
            <div key={d} className="mb-5">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F] mb-1.5 flex items-center gap-1.5">
                <ShieldWarning size={11} weight="fill" /> {d}
                <span className="text-slate-400 font-mono">({(grouped[d] || []).length})</span>
              </div>
              <div className="rounded-sm border border-slate-200 divide-y divide-gray-100 bg-white shadow-sm overflow-hidden">
                {(grouped[d] || []).map((u) => (
                  <button key={u.id} onClick={() => run(u)}
                    data-testid={`p2p-uc-${u.id}`}
                    className={`w-full text-left px-3 py-2.5 transition-colors ${selected?.id === u.id ? "bg-[#0B1E3F]/5 border-l-4 border-[#0B1E3F]" : "border-l-4 border-transparent hover:bg-slate-50"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[12px] font-medium truncate">{u.name}</div>
                      <span className={`shrink-0 text-[9px] font-bold tracking-wider uppercase border rounded-md px-1.5 py-0.5 ${RISK_COLOR[u.risk] || RISK_COLOR.Low}`}>
                        {u.risk}
                      </span>
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <span>{u.id} · {u.priority}</span>
                      {u.has_detector && (
                        <span className="inline-flex items-center gap-0.5 text-[8px] font-bold tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-px rounded">
                          <Sparkle size={8} weight="fill" /> AI rule
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        {/* Right — selected use case + results */}
        <section className="min-w-0">
          {!selected ? (
            <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-12 text-center" data-testid="p2p-empty">
              <div className="mb-3 inline-block p-3 rounded-sm bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100">
                <ShieldWarning size={28} weight="duotone" className="text-[#0B1E3F]" />
              </div>
              <div className="text-sm font-semibold text-slate-900">Pick a use case to start scanning</div>
              <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Choose any of the 39 compliance use cases from the left to fetch live Oracle Fusion data and run its AI rule.</div>
            </div>
          ) : (
            <>
              <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-6 hover:shadow-md transition-shadow" data-testid="p2p-detail">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F]">
                      {selected.domain} · {selected.id} · {selected.priority}
                    </div>
                    <h2 className="text-2xl font-black tracking-tight mt-1">{selected.name}</h2>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed">{selected.description}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-bold tracking-wider uppercase border rounded-md px-2 py-1 ${RISK_COLOR[selected.risk]}`}>
                    {selected.risk} risk
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-5">
                  <Row k="Trigger" v={selected.trigger} />
                  <Row k="Fusion module" v={selected.fusion_module} />
                  <Row k="Feasibility" v={<span className={`font-bold ${FEAS_COLOR[selected.feasibility]}`}>{selected.feasibility}</span>} />
                  <Row k="Fusion endpoint" v={<span className="font-mono text-[11px]">{selected.fusion_path}</span>} />
                </div>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  <button onClick={() => run(selected)} disabled={busy}
                    data-testid="p2p-run-btn"
                    className="inline-flex items-center gap-1.5 rounded-sm bg-gradient-to-r from-[#0B1E3F] to-[#3B5BFF] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0">
                    <Play size={12} weight="fill" /> {busy ? "Fetching from Fusion…" : "Run on Fusion"}
                  </button>
                  {result && (
                    <span className="text-[11px] font-mono text-emerald-700 flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> {result.fetched} record{result.fetched === 1 ? "" : "s"} fetched (HTTP {result.oracle_status})
                    </span>
                  )}
                  {result && result.detector && (
                    <span className="text-[10px] font-mono text-slate-600 flex items-center gap-1">
                      <Target size={11} weight="bold" /> Detector: <span className="font-bold">{result.detector}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Findings — anomaly detection results */}
              {result && result.finding_count > 0 && (
                <div className="mt-4 rounded-sm border border-red-200 bg-white shadow-sm overflow-hidden" data-testid="p2p-findings">
                  <div className="bg-gradient-to-r from-red-50 via-rose-50 to-amber-50 px-4 py-3 border-b border-red-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-sm bg-red-100/70">
                        <Warning size={18} weight="fill" className="text-[#DC2626]" />
                      </div>
                      <div>
                        <div className="text-sm font-black tracking-tight text-[#DC2626]">
                          {result.finding_count} anomaly finding{result.finding_count === 1 ? "" : "s"}
                        </div>
                        <div className="text-[11px] text-slate-700">
                          Rule-based detection on {result.fetched} sampled records
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {Object.entries(result.severity_counts || {}).map(([sev, count]) => (
                        <span key={sev}
                          data-testid={`p2p-severity-${sev.toLowerCase()}`}
                          className={`text-[10px] font-bold uppercase tracking-wider text-white px-2 py-1 rounded-md shadow-sm ${SEVERITY_BG[sev] || SEVERITY_BG.Low}`}>
                          {sev} · {count}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">
                    {result.findings.map((f, i) => (
                      <details key={i} className="px-4 py-3 group hover:bg-slate-50/60 transition-colors" data-testid={`p2p-finding-${i}`}>
                        <summary className="cursor-pointer flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <span className={`shrink-0 mt-0.5 text-[9px] font-bold uppercase tracking-wider text-white px-1.5 py-0.5 rounded-md ${SEVERITY_BG[f.severity] || SEVERITY_BG.Low}`}>
                              {f.severity}
                            </span>
                            <div className="min-w-0">
                              <div className="text-[12px] font-medium text-slate-900">{f.reason}</div>
                              {f.evidence && (
                                <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
                                  {Object.entries(f.evidence).slice(0, 4).map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join("  ·  ")}
                                </div>
                              )}
                            </div>
                          </div>
                          <CaretRight size={12} weight="bold" className="shrink-0 transition-transform group-open:rotate-90 mt-1" />
                        </summary>
                        <pre className="mt-2 text-[11px] font-mono bg-[#0B1733] text-emerald-300 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(f.record, null, 2)}
                        </pre>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* When detector ran but found nothing */}
              {result && result.detector && result.finding_count === 0 && result.items && result.items.length > 0 && (
                <div className="mt-4 rounded-sm border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 text-[12px] text-emerald-800 flex items-center gap-3 shadow-sm" data-testid="p2p-no-findings">
                  <div className="p-2 rounded-sm bg-emerald-100/70">
                    <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold">No anomalies detected.</div>
                    <div className="text-[11px] text-emerald-700 mt-0.5">
                      The <span className="font-mono">{result.detector}</span> rule was evaluated against {result.fetched} record(s) and nothing tripped it.
                    </div>
                  </div>
                </div>
              )}

              {/* Note for use cases without a programmatic detector */}
              {result && !result.detector && result.note && (
                <div className="mt-4 rounded-sm border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 text-[12px] text-[#0B1E3F] flex items-start gap-3 shadow-sm" data-testid="p2p-no-detector">
                  <Sparkle size={14} weight="fill" className="shrink-0 mt-0.5" />
                  <div>{result.note}</div>
                </div>
              )}

              {err && (
                <div className="mt-4 rounded-sm border border-red-200 bg-gradient-to-r from-red-50 to-rose-50 p-4 text-[12px] text-[#DC2626] flex items-start gap-3 shadow-sm" data-testid="p2p-error">
                  <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div>{err}</div>
                    {profileMissing && (
                      <button onClick={() => setSetupOpen(true)}
                        data-testid="p2p-configure-oracle"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-[#DC2626] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-sm hover:bg-[#B91C1C]">
                        <Gear size={12} weight="bold" /> Configure Oracle Profile
                      </button>
                    )}
                  </div>
                </div>
              )}

              {result && result.items && result.items.length > 0 && (
                <div className="mt-4 rounded-sm border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="p2p-result-list">
                  <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 border-b border-slate-200">
                    Sample records · {result.items.length} of {result.fetched}
                  </div>
                  <div className="divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">
                    {result.items.map((it, i) => (
                      <details key={i} className="px-4 py-2.5 group hover:bg-slate-50/60 transition-colors">
                        <summary className="cursor-pointer text-[12px] font-medium text-slate-800 flex items-center justify-between gap-3">
                          <span className="truncate font-mono">
                            {Object.entries(it).slice(0, 4).map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join("  ·  ")}
                          </span>
                          <CaretRight size={12} weight="bold" className="shrink-0 transition-transform group-open:rotate-90" />
                        </summary>
                        <pre className="mt-2 text-[11px] font-mono bg-[#0B1733] text-emerald-300 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(it, null, 2)}
                        </pre>
                      </details>
                    ))}
                  </div>
                </div>
              )}
              {result && (!result.items || result.items.length === 0) && (
                <div className="mt-4 rounded-sm border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-6 text-center shadow-sm" data-testid="p2p-empty-results">
                  <div className="inline-block p-3 rounded-sm bg-emerald-100/70 mb-2">
                    <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                  </div>
                  <div className="font-semibold text-sm text-emerald-900">Fusion responded — no matching records.</div>
                  <div className="text-[12px] text-emerald-700 mt-1">
                    Your Oracle profile is working. This use case&apos;s filter didn&apos;t match any rows in this Fusion instance — try another use case or a different tenant.
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div>
    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-0.5">{k}</div>
    <div className="text-[12px] text-slate-800">{v || "—"}</div>
  </div>
);
